import { useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Building2,
  DollarSign,
  Clock,
  Percent,
  AlertCircle,
  Globe,
  Shield,
  Bell,
  CreditCard,
  Timer,
  Lock,
  Users,
  Banknote,
  CalendarClock,
  ToggleLeft,
} from '@/lib/icons';

import { useToast } from '@/hooks/use-toast';
import { useAppDispatch } from '@/store/hooks';
import { setTenantSettings as setTenantSettingsRedux } from '@/features/tenants/tenant-slice';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { ButtonSpinner } from '@/components/feedback';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Badge } from '@/components/ui/badge';

import {
  getTenantSettings,
  updateTenantSettings,
} from '@/features/tenants/api';

export const Route = createFileRoute('/_layout/settings/tenant')({
  component: TenantSettingsPage,
});

const CURRENCY_OPTIONS = ['USD', 'KES', 'SOS', 'EUR'] as const;
const TIMEZONE_OPTIONS = [
  'Africa/Mogadishu',
  'Africa/Nairobi',
  'Europe/Helsinki',
  'UTC',
] as const;
const LOCALE_OPTIONS = ['en', 'so'] as const;

const PAYMENT_METHOD_OPTIONS = [
  { value: 'mobile_money', label: 'Mobile Money' },
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
] as const;

const createTenantSettingsSchema = (t: (key: string) => string) =>
  z.object({
    companyName: z
      .string()
      .min(2, t('settings.tenant.validation.companyNameRequired')),
    currency: z
      .string()
      .min(1, t('settings.tenant.validation.currencyRequired')),
    timezone: z
      .string()
      .min(1, t('settings.tenant.validation.timezoneRequired')),
    locale: z.string().min(1, t('settings.tenant.validation.localeRequired')),
    defaultMarkup: z.coerce
      .number()
      .min(0, t('settings.tenant.validation.markupMin'))
      .max(100, t('settings.tenant.validation.markupMax')),
    lateFeePercent: z.coerce
      .number()
      .min(0, t('settings.tenant.validation.lateFeeMin'))
      .max(100, t('settings.tenant.validation.lateFeeMax')),
    // Business Configuration
    gracePeriodDays: z.coerce.number().min(0).max(30),
    cancellationWindowHours: z.coerce.number().min(1).max(720),
    autoLockEnabled: z.boolean(),
    autoLockAfterDays: z.coerce.number().min(1).max(90),
    maxActiveContracts: z.coerce.number().min(1).max(50),
    // Payment Configuration
    paymentMethods: z
      .array(z.string())
      .min(1, 'At least one payment method required'),
    paymentReminderDaysBefore: z.coerce.number().min(0).max(30),
    allowPartialPayments: z.boolean(),
    // Notification Preferences
    notifyOnApplicationSubmitted: z.boolean(),
    notifyOnPaymentReceived: z.boolean(),
    notifyOnPaymentOverdue: z.boolean(),
    notifyOnContractCompleted: z.boolean(),
  });

type TenantSettingsFormValues = z.infer<
  ReturnType<typeof createTenantSettingsSchema>
>;

/* ── Section Header Component ───────────────────────────── */
function SectionHeader({
  icon: Icon,
  title,
  description,
  gradient,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  gradient: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={`p-2 rounded-xl bg-gradient-to-br ${gradient} text-white shadow-lg`}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription className="text-xs mt-0.5">
          {description}
        </CardDescription>
      </div>
    </div>
  );
}

/* ── Toggle Row Component ───────────────────────────────── */
function ToggleRow({
  icon: Icon,
  label,
  description,
  checked,
  onCheckedChange,
}: {
  icon: React.ElementType;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-3">
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
        <div>
          <p className="text-sm font-medium">{label}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

function TenantSettingsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const TenantSettingsSchema = createTenantSettingsSchema(t);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: getTenantSettings,
  });

  const settings = data?.value;

  const form = useForm<TenantSettingsFormValues>({
    resolver: zodResolver(TenantSettingsSchema),
    defaultValues: {
      companyName: '',
      currency: '',
      timezone: '',
      locale: '',
      defaultMarkup: 0,
      lateFeePercent: 0,
      gracePeriodDays: 3,
      cancellationWindowHours: 48,
      autoLockEnabled: true,
      autoLockAfterDays: 7,
      maxActiveContracts: 3,
      paymentMethods: ['mobile_money', 'cash'],
      paymentReminderDaysBefore: 3,
      allowPartialPayments: false,
      notifyOnApplicationSubmitted: true,
      notifyOnPaymentReceived: true,
      notifyOnPaymentOverdue: true,
      notifyOnContractCompleted: true,
    },
  });

  useEffect(() => {
    if (settings) {
      form.reset(settings);
    }
  }, [settings, form]);

  const saveMutation = useMutation({
    mutationFn: (values: TenantSettingsFormValues) =>
      updateTenantSettings(values),
    onSuccess: (result) => {
      if (result.succeeded) {
        queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
        // Sync saved settings to Redux store for global reactivity
        if (result.value) {
          dispatch(setTenantSettingsRedux(result.value));
        }
        toast({
          title: t('common.success'),
          description: t('settings.tenant.toast.saved'),
        });
      } else {
        toast({
          title: t('common.error'),
          description: result.errors?.[0] ?? t('settings.tenant.toast.error'),
          variant: 'destructive',
        });
      }
    },
  });

  const onSubmit = (values: TenantSettingsFormValues) => {
    saveMutation.mutate(values);
  };

  const handleReset = () => {
    if (settings) {
      form.reset(settings);
    }
  };

  const togglePaymentMethod = (method: string, enabled: boolean) => {
    const current = form.getValues('paymentMethods');
    if (enabled) {
      form.setValue('paymentMethods', [...current, method], {
        shouldDirty: true,
      });
    } else {
      const filtered = current.filter((m) => m !== method);
      if (filtered.length > 0) {
        form.setValue('paymentMethods', filtered, { shouldDirty: true });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64 mt-2" />
          </div>
          <Skeleton className="h-10 w-10 rounded-xl" />
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="shadow-md border-0">
              <CardHeader className="pb-3">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-56 mt-1" />
              </CardHeader>
              <CardContent className="pt-2 space-y-5">
                {[1, 2, 3].map((j) => (
                  <div key={j} className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {t('settings.tenant.title')}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {t('settings.tenant.description')}
            </p>
          </div>
        </div>
        <ApiErrorCard
          error={error}
          onRetry={() => refetch()}
          title="Failed to load tenant settings"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('settings.tenant.title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t('settings.tenant.description')}
          </p>
        </div>
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-primary-foreground shadow-lg">
          <Building2 className="h-5 w-5" />
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* ── Business Information ────────────────────────── */}
            <Card className="shadow-md border-0">
              <CardHeader className="pb-3">
                <SectionHeader
                  icon={Building2}
                  title="Business Information"
                  description="Core company and regional settings"
                  gradient="from-indigo-500 to-indigo-600"
                />
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="companyName"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel className="text-sm font-medium flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                        {t('settings.tenant.companyName')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t(
                            'settings.tenant.placeholders.companyName'
                          )}
                          className="font-medium"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel className="text-sm font-medium flex items-center gap-2">
                        <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                        {t('settings.tenant.currency')}
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="font-medium">
                            <SelectValue
                              placeholder={t(
                                'settings.tenant.placeholders.currency'
                              )}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CURRENCY_OPTIONS.map((currency) => (
                            <SelectItem key={currency} value={currency}>
                              {t(`settings.tenant.currencies.${currency}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="timezone"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel className="text-sm font-medium flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                        {t('settings.tenant.timezone')}
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="font-medium">
                            <SelectValue
                              placeholder={t(
                                'settings.tenant.placeholders.timezone'
                              )}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TIMEZONE_OPTIONS.map((tz) => (
                            <SelectItem key={tz} value={tz}>
                              {t(`settings.tenant.timezones.${tz}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="locale"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel className="text-sm font-medium flex items-center gap-2">
                        <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                        {t('settings.tenant.locale')}
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="font-medium">
                            <SelectValue
                              placeholder={t(
                                'settings.tenant.placeholders.locale'
                              )}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {LOCALE_OPTIONS.map((locale) => (
                            <SelectItem key={locale} value={locale}>
                              {t(`settings.tenant.locales.${locale}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* ── Financing & Risk ──────────────────────────── */}
            <Card className="shadow-md border-0">
              <CardHeader className="pb-3">
                <SectionHeader
                  icon={CreditCard}
                  title="Financing & Risk"
                  description="Markup rates, penalties, and contract limits"
                  gradient="from-violet-500 to-violet-600"
                />
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="defaultMarkup"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel className="text-sm font-medium flex items-center gap-2">
                        <Percent className="h-3.5 w-3.5 text-muted-foreground" />
                        {t('settings.tenant.defaultMarkup')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          className="font-medium"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Default Murabaha markup applied to new plans
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lateFeePercent"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel className="text-sm font-medium flex items-center gap-2">
                        <AlertCircle className="h-3.5 w-3.5 text-muted-foreground" />
                        {t('settings.tenant.lateFeePercent')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          className="font-medium"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Penalty rate applied to overdue payments
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gracePeriodDays"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel className="text-sm font-medium flex items-center gap-2">
                        <Timer className="h-3.5 w-3.5 text-muted-foreground" />
                        Grace Period (days)
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          className="font-medium"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Days after due date before late fee applies
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="cancellationWindowHours"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel className="text-sm font-medium flex items-center gap-2">
                        <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                        Cancellation Window (hours)
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          className="font-medium"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Hours after signing during which a contract can still be
                        cancelled. The deposit is non-refundable either way.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="maxActiveContracts"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5">
                      <FormLabel className="text-sm font-medium flex items-center gap-2">
                        <Users className="h-3.5 w-3.5 text-muted-foreground" />
                        Max Active Contracts
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          className="font-medium"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Maximum concurrent contracts per customer
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* ── Device Lock & Payment ─────────────────────── */}
            <Card className="shadow-md border-0">
              <CardHeader className="pb-3">
                <SectionHeader
                  icon={Shield}
                  title="Device Lock & Payments"
                  description="Auto-lock policies and payment configuration"
                  gradient="from-amber-500 to-amber-600"
                />
              </CardHeader>
              <CardContent className="space-y-1">
                <FormField
                  control={form.control}
                  name="autoLockEnabled"
                  render={({ field }) => (
                    <ToggleRow
                      icon={Lock}
                      label="Auto-Lock Devices"
                      description="Automatically lock device on overdue payments"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />

                {form.watch('autoLockEnabled') && (
                  <FormField
                    control={form.control}
                    name="autoLockAfterDays"
                    render={({ field }) => (
                      <FormItem className="space-y-1.5 pl-7 pb-3">
                        <FormLabel className="text-sm font-medium flex items-center gap-2">
                          <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                          Lock After (days overdue)
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            className="font-medium max-w-[200px]"
                            {...field}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />

                <FormField
                  control={form.control}
                  name="allowPartialPayments"
                  render={({ field }) => (
                    <ToggleRow
                      icon={Banknote}
                      label="Allow Partial Payments"
                      description="Let customers pay less than the full installment"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />

                <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />

                <FormField
                  control={form.control}
                  name="paymentReminderDaysBefore"
                  render={({ field }) => (
                    <FormItem className="space-y-1.5 py-3">
                      <FormLabel className="text-sm font-medium flex items-center gap-2">
                        <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                        Payment Reminder
                      </FormLabel>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          className="font-medium max-w-[100px]"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                        <span className="text-sm text-muted-foreground">
                          days before due date
                        </span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />

                {/* Payment Methods */}
                <div className="py-3">
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                    Accepted Payment Methods
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {PAYMENT_METHOD_OPTIONS.map((method) => {
                      const isActive = form
                        .watch('paymentMethods')
                        .includes(method.value);
                      return (
                        <button
                          key={method.value}
                          type="button"
                          onClick={() =>
                            togglePaymentMethod(method.value, !isActive)
                          }
                          className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition-all duration-200 ${
                            isActive
                              ? 'bg-primary/15 text-primary ring-1 ring-primary/30'
                              : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                          }`}
                        >
                          {isActive && (
                            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                          )}
                          {method.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ── Notifications ─────────────────────────────── */}
            <Card className="shadow-md border-0">
              <CardHeader className="pb-3">
                <SectionHeader
                  icon={Bell}
                  title="Notifications"
                  description="Control which events trigger notifications"
                  gradient="from-emerald-500 to-emerald-600"
                />
              </CardHeader>
              <CardContent className="space-y-0">
                <FormField
                  control={form.control}
                  name="notifyOnApplicationSubmitted"
                  render={({ field }) => (
                    <ToggleRow
                      icon={ToggleLeft}
                      label="Application Submitted"
                      description="Notify when a new application is submitted"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
                <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />

                <FormField
                  control={form.control}
                  name="notifyOnPaymentReceived"
                  render={({ field }) => (
                    <ToggleRow
                      icon={DollarSign}
                      label="Payment Received"
                      description="Notify when an installment payment is collected"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
                <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />

                <FormField
                  control={form.control}
                  name="notifyOnPaymentOverdue"
                  render={({ field }) => (
                    <ToggleRow
                      icon={AlertCircle}
                      label="Payment Overdue"
                      description="Notify when an installment becomes overdue"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
                <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />

                <FormField
                  control={form.control}
                  name="notifyOnContractCompleted"
                  render={({ field }) => (
                    <ToggleRow
                      icon={Shield}
                      label="Contract Completed"
                      description="Notify when all payments are made and device unlocks"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </CardContent>
            </Card>
          </div>

          {/* ── Action Bar ──────────────────────────────────── */}
          <div className="sticky bottom-0 z-10 flex items-center justify-between gap-3 rounded-xl bg-[hsl(var(--surface)/0.9)] backdrop-blur-lg px-5 py-3.5 ambient-shadow">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {form.formState.isDirty ? (
                <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30 text-xs">
                  Unsaved changes
                </Badge>
              ) : (
                <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 text-xs">
                  All saved
                </Badge>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                disabled={!form.formState.isDirty}
              >
                {t('settings.tenant.reset')}
              </Button>
              <Button type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending && (
                  <ButtonSpinner className="mr-2" />
                )}
                {t('common.actions.save')}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
